/**
 * services/api.js
 * Axios client for communicating with FastAPI backend.
 * 
 * All API calls go through this service to maintain centralized
 * error handling, authentication, and request/response formatting.
 */

import axios from 'axios';

// Prefer Vite proxy in development to avoid CORS issues between localhost/127.0.0.1.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Normalize axios errors to a consistent shape consumed by UI.
 */
const toClientError = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return { error: responseData };
  }

  return {
    error:
      responseData?.error ||
      responseData?.detail ||
      error?.message ||
      fallbackMessage,
    httpStatus: error?.response?.status,
  };
};

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for long-running processes
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor — attach JWT token to every request
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor — handle errors + auto-logout on 401
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('API Error:', error.response.status, error.response.data);
      if (error.response.status === 401) {
        // Skip auto-logout for login/register requests — a 401 there just means
        // wrong credentials and should be shown as a form error, not a redirect.
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
        if (!isAuthEndpoint) {
          // Token expired or invalid — clear auth and reload to show login page
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_username');
          localStorage.removeItem('auth_email');
          localStorage.removeItem('auth_role');
          window.location.reload();
        }
      }
    } else if (error.request) {
      console.error('Network Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * API Service Object
 * Contains all API endpoints as methods
 */
const API = {
  
  /**
   * Upload an engineering drawing file
   * 
   * @param {File} file - The file to upload
   * @returns {Promise} { file_id, file_name, status, message }
   */
  upload: async (file, onProgress, batchId = null, batchName = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (batchId)   formData.append('batch_id',   batchId);
    if (batchName) formData.append('batch_name', batchName);
    
    try {
      const response = await apiClient.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
        onUploadProgress: (event) => {
          if (typeof onProgress !== 'function') {
            return;
          }
          if (!event.total) {
            onProgress(50);
            return;
          }
          const percent = Math.min(100, Math.round((event.loaded * 100) / event.total));
          onProgress(percent);
        },
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Upload failed');
    }
  },

  /**
   * Start processing an uploaded file
   * 
   * @param {number} fileId - ID from upload response
   * @param {string} apiKey - Gemini API key
   * @returns {Promise} { file_id, status, progress, message }
   */
  process: async (fileId, apiKey, ccSymbols = ['spade']) => {
    try {
      const symbols = Array.isArray(ccSymbols) ? ccSymbols : [ccSymbols];
      const response = await apiClient.post(`/process/${fileId}`, {
        file_id:    fileId,
        api_key:    apiKey,
        cc_symbol:  symbols[0],
        cc_symbols: symbols,
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Processing failed');
    }
  },

  /**
   * Poll processing status
   * 
   * @param {number} fileId - ID of the file being processed
   * @returns {Promise} { file_id, status, progress, message, error_message }
   */
  getProcessStatus: async (fileId) => {
    try {
      const response = await apiClient.get(`/process-status/${fileId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Status check failed');
    }
  },

  /**
   * Get complete inspection plan results
   * 
   * @param {number} fileId - ID of the processed file
   * @returns {Promise} {
   *   file_id, file_name, status,
   *   metadata, inspection_items, cc_count, item_count,
   *   excel_file_path, upload_timestamp, completed_timestamp
   * }
   */
  getResults: async (fileId) => {
    try {
      const response = await apiClient.get(`/results/${fileId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to fetch results');
    }
  },

  /**
   * Get list of all previous results (history)
   * 
   * @param {number} skip - Pagination offset
   * @param {number} limit - Max results to return
   * @returns {Promise} Array of result items
   */
  getHistory: async (skip = 0, limit = 50) => {
    try {
      const response = await apiClient.get(`/results/history/all`, {
        params: { skip, limit },
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to fetch history');
    }
  },

  /**
   * Delete a scan record from history
   *
   * @param {number} fileId - ID of the scan to delete
   * @returns {Promise} { message }
   */
  deleteHistoryItem: async (fileId) => {
    try {
      const response = await apiClient.delete(`/results/${fileId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to delete scan');
    }
  },

  /**
   * Delete selected scan records from history
   *
   * @param {number[]} fileIds - IDs of scans to delete
   * @returns {Promise} { message, deleted_count }
   */
  deleteSelectedHistoryItems: async (fileIds) => {
    try {
      const response = await apiClient.delete('/results', {
        data: { ids: fileIds },
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to delete selected scans');
    }
  },

  /**
   * Delete all scan records from history
   *
   * @returns {Promise} { message, deleted_count }
   */
  deleteAllHistoryItems: async () => {
    try {
      const response = await apiClient.delete('/results/history/all');
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to delete all scans');
    }
  },

  /**
   * Get overall statistics
   * 
   * @returns {Promise} {
   *   total_uploads, completed, failed,
   *   total_inspection_items, total_cc_symbols
   * }
   */
  getStats: async () => {
    try {
      const response = await apiClient.get('/results/stats');
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to fetch stats');
    }
  },

  /**
   * Persist user-edited inspection items and metadata back to the server.
   *
   * @param {number} fileId - ID of the scan result
   * @param {{ inspection_items: object[], metadata: object }} payload
   */
  updateResults: async (fileId, payload) => {
    try {
      const response = await apiClient.patch(`/results/${fileId}`, payload);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to save results');
    }
  },

  updateBlockTolerances: async (fileId, blockTolerances) => {
    try {
      const response = await apiClient.patch(`/results/${fileId}`, { block_tolerances: blockTolerances });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to save block tolerances');
    }
  },

  patchTabularVariant: async (fileId, index, payload) => {
    try {
      const response = await apiClient.patch(`/tabular/${fileId}/${index}`, payload);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to save tabular variant');
    }
  },

  /**
   * Download Excel inspection plan file
   * 
   * @param {number} fileId - ID of the file
   * @param {string} filename - Filename to save as
   */
  downloadExcel: async (fileId, filename) => {
    try {
      const response = await apiClient.get(`/download/excel/${fileId}`, {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `Inspection_Plan_${fileId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      throw toClientError(error, 'Download failed');
    }
  },

  /**
   * Download PDF inspection plan report
   *
   * @param {number} fileId - ID of the file
   * @param {string} filename - Filename to save as
   */
  downloadPdf: async (fileId, filename) => {
    try {
      const response = await apiClient.get(`/download/pdf/${fileId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `Inspection_Plan_${fileId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'PDF download failed');
    }
  },

  /**
   * Download all inspection plans as a ZIP of Excel files.
   * @param {number[]} fileIds - Array of completed file IDs
   */
  downloadZipExcel: async (fileIds) => {
    try {
      const response = await apiClient.post(
        '/download/zip/excel',
        { file_ids: fileIds },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inspection_plans_excel.zip');
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'ZIP download failed');
    }
  },

  /**
   * Download all inspection plans as a ZIP of PDF files.
   * @param {number[]} fileIds - Array of completed file IDs
   */
  downloadZipPdf: async (fileIds) => {
    try {
      const response = await apiClient.post(
        '/download/zip/pdf',
        { file_ids: fileIds },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inspection_plans_pdf.zip');
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'ZIP download failed');
    }
  },

  /**
   * Download annotated image
   * 
   * @param {number} fileId - ID of the file
   */
  downloadAnnotatedImage: async (fileId) => {
    try {
      const response = await apiClient.get(`/download/annotated/${fileId}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `drawing_${fileId}_original.png`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      throw toClientError(error, 'Image download failed');
    }
  },

  /**
   * Get annotated image for display
   */
  getAnnotatedImage: async (fileId) => {
    try {
      const response = await apiClient.get(`/annotate/${fileId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Image fetch failed');
    }
  },

  /**
   * Cancel an in-progress processing job.
   * Marks the scan as failed in the database so the user must re-upload.
   *
   * @param {number} fileId - ID of the file being processed
   * @returns {Promise} { file_id, status, progress, message }
   */
  cancelProcessing: async (fileId) => {
    try {
      const response = await apiClient.post(`/cancel/${fileId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Cancel failed');
    }
  },

  /**
   * Login with username + password — returns { access_token, username, email }
   */
  login: async (username, password) => {
    try {
      const response = await apiClient.post('/auth/login', { username, password });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Login failed');
    }
  },

  /**
   * Register a new account — returns { access_token, username, email, role }
   */
  register: async (username, email, password, role = 'uploader') => {
    try {
      const response = await apiClient.post('/auth/register', { username, email, password, role });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Registration failed');
    }
  },

  // ── Share & Review (v1.2) ─────────────────────────────────────────────────

  /**
   * Create a review request for a completed scan.
   * @param {{ drawing_id, action, reviewer_email? }} payload
   */
  share: async (payload) => {
    try {
      const response = await apiClient.post('/share', payload);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Share failed');
    }
  },

  /**
   * Fetch review token data — public endpoint, no auth header required.
   * @param {string} token - Share token from the review link
   */
  getReviewToken: async (token) => {
    try {
      // Use raw axios (no auth interceptor) so unauthenticated reviewers can call it
      const response = await axios.get(`${API_BASE_URL}/review/${token}`, { timeout: 30000 });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load review');
    }
  },

  /**
   * Approve a review request.
   * @param {string} token
   * @param {string|null} comment
   */
  approveReview: async (token, comment, drawingId = null) => {
    try {
      const body = { comment };
      if (drawingId !== null) body.drawing_id = drawingId;
      const response = await apiClient.post(`/review/${token}/approve`, body);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Approval failed');
    }
  },

  /**
   * Reject a review request (comment is mandatory, enforced by backend).
   * @param {string} token
   * @param {string} comment - Required rejection reason
   * @param {number|null} drawingId - For batch reviews: the specific file to reject
   */
  rejectReview: async (token, comment, drawingId = null) => {
    try {
      const body = { comment };
      if (drawingId !== null) body.drawing_id = drawingId;
      const response = await apiClient.post(`/review/${token}/reject`, body);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Rejection failed');
    }
  },

  /**
   * Get all review requests assigned to the logged-in reviewer (by email).
   */
  getReviewerDashboard: async () => {
    try {
      const response = await apiClient.get('/dashboard/reviewer');
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load reviewer dashboard');
    }
  },

  deleteReview: async (reviewId) => {
    try {
      const response = await apiClient.delete(`/review/${reviewId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to delete review');
    }
  },

  /**
   * Get full lifecycle event trail for a single drawing.
   */
  getLifecycle: async (drawingId) => {
    try {
      const response = await apiClient.get(`/lifecycle/${drawingId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load lifecycle');
    }
  },

  getLifecycleBatch: async (batchId) => {
    try {
      const response = await apiClient.get(`/lifecycle/batch/${batchId}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load batch lifecycle');
    }
  },

  /**
   * Get review details (reviewer name, email, comments) for a drawing.
   * Uses raw axios to avoid the 401-logout interceptor — this is an optional
   * enrichment call and must never trigger a forced sign-out.
   * @param {number} drawingId
   */
  getDrawingReviewDetails: async (drawingId) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;
      const response = await axios.get(`${API_BASE_URL}/review/drawing/${drawingId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      return response.data;
    } catch {
      return null;
    }
  },

  // ── Business Rules (v1.3) ─────────────────────────────────────────────────

  /** Return all 16 spec categories with display labels and system defaults. */
  getBusinessRulesCategories: async () => {
    try {
      const response = await apiClient.get('/business-rules/categories');
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load categories');
    }
  },

  /** Return all saved business rules for the current user. */
  getBusinessRules: async () => {
    try {
      const response = await apiClient.get('/business-rules');
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load business rules');
    }
  },

  /**
   * Upsert a business rule for a (spec_category, spec_sub_type) pair.
   * @param {object} rule - Full rule object including spec_category, spec_sub_type, spec_type
   */
  upsertBusinessRule: async (rule) => {
    try {
      const response = await apiClient.post('/business-rules', rule);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to save rule');
    }
  },

  /**
   * Update a single (category, sub-type) rule.
   * @param {string} category - spec_category key
   * @param {string} subType  - spec_sub_type value
   * @param {object} rule     - Updated rule fields
   */
  updateBusinessRule: async (category, subType, rule) => {
    try {
      const response = await apiClient.put(`/business-rules/${category}/${encodeURIComponent(subType)}`, rule);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to update rule');
    }
  },

  /**
   * Delete a (category, sub-type) rule — reverts to system default on next scan.
   * @param {string} category - spec_category key
   * @param {string} subType  - spec_sub_type value
   */
  deleteBusinessRule: async (category, subType) => {
    try {
      const response = await apiClient.delete(`/business-rules/${category}/${encodeURIComponent(subType)}`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to delete rule');
    }
  },

  // ── Custom Tools ──────────────────────────────────────────────────────────

  /** Return all custom measurement tools for the authenticated user. */
  getCustomTools: async () => {
    try {
      const response = await apiClient.get('/custom-tools');
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to load custom tools');
    }
  },

  /**
   * Add a new custom measurement tool.
   * @param {string} toolName - Display name for the tool
   */
  addCustomTool: async (toolName) => {
    try {
      const response = await apiClient.post('/custom-tools', { tool_name: toolName });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to add custom tool');
    }
  },

  /**
   * Delete a custom tool by ID.
   * @param {number} id - Tool ID to delete
   */
  deleteCustomTool: async (id) => {
    try {
      await apiClient.delete(`/custom-tools/${id}`);
    } catch (error) {
      throw toClientError(error, 'Failed to delete custom tool');
    }
  },

  // ── Tabular Scan ──────────────────────────────────────────────────────────

  /**
   * Start a Tabular Scan for an already-uploaded assembly drawing.
   * @param {number} fileId  - ID returned by /upload
   * @param {string} apiKey  - Gemini API key
   */
  startTabularScan: async (fileId, apiKey, ccSymbols = []) => {
    try {
      const response = await apiClient.post(`/tabular-scan/${fileId}`, {
        api_key: apiKey,
        cc_symbols: Array.isArray(ccSymbols) ? ccSymbols : [],
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to start tabular scan');
    }
  },

  /**
   * Download the tabular inspection plan workbook (.xlsx).
   * @param {number} fileId    - ID of the completed tabular scan
   * @param {string} filename  - Suggested download filename
   */
  downloadTabularExcel: async (fileId, filename) => {
    try {
      const response = await apiClient.get(`/download/tabular-excel/${fileId}`, {
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', filename || `Tabular_Inspection_Plan_${fileId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'Tabular Excel download failed');
    }
  },

  /**
   * Tabular Scan v2 — list every part (variant) produced, each with its full
   * normal-scan `final_plan` (inspection_items, gdt_table_rows, notes_items…).
   * @param {number} fileId
   * @returns {Promise<{file_id:number, variant_count:number, variants:Array}>}
   */
  getTabularVariants: async (fileId) => {
    try {
      const response = await apiClient.get(`/tabular/${fileId}/variants`);
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to fetch tabular variants');
    }
  },

  /** Download one part's Excel by variant index. */
  downloadTabularVariantExcel: async (fileId, variantIndex, filename) => {
    try {
      const response = await apiClient.get(`/tabular/${fileId}/${variantIndex}/excel`, {
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', filename || `Variant_${variantIndex}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'Variant Excel download failed');
    }
  },

  /** Download one part's PDF by variant index. */
  downloadTabularVariantPdf: async (fileId, variantIndex, filename) => {
    try {
      const response = await apiClient.get(`/tabular/${fileId}/${variantIndex}/pdf`, {
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', filename || `Variant_${variantIndex}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'Variant PDF download failed');
    }
  },

  /** Download every part's PDF (or Excel) bundled as a ZIP. */
  downloadTabularZip: async (fileId, type = 'pdf') => {
    try {
      const response = await apiClient.get(`/download/tabular-zip/${fileId}`, {
        params: { type },
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `tabular_inspection_plans_${type}.zip`);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      throw toClientError(error, 'ZIP download failed');
    }
  },

  /** Promote a low-confidence row to Main/GD&T/Notes for a NORMAL scan. */
  promoteResultsRow: async (fileId, lcIndex, destination, overrides = {}) => {
    try {
      const response = await apiClient.post(`/results/${fileId}/promote`, {
        lc_index: lcIndex, destination, overrides,
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to share row');
    }
  },

  /** Move a notes_items row to Low-Confidence for a NORMAL scan. */
  demoteResultsNote: async (fileId, noteLabel, category = '') => {
    try {
      const response = await apiClient.post(`/results/${fileId}/demote-note`, { note_label: noteLabel, category });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to move note to Dimensions for Review');
    }
  },

  /** Move a gdt_table_rows row to Low-Confidence for a NORMAL scan. */
  demoteResultsGdt: async (fileId, gdtItemNo) => {
    try {
      const response = await apiClient.post(`/results/${fileId}/demote-gdt`, { gdt_item_no: gdtItemNo });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to move GD&T row to Dimensions for Review');
    }
  },

  /** Move a notes_items row to Low-Confidence for a TABULAR part. */
  demoteTabularNote: async (fileId, variantIndex, noteLabel, category = '') => {
    try {
      const response = await apiClient.post(`/tabular/${fileId}/${variantIndex}/demote-note`, { note_label: noteLabel, category });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to move note to Dimensions for Review');
    }
  },

  /** Move a gdt_table_rows row to Low-Confidence for a TABULAR part. */
  demoteTabularGdt: async (fileId, variantIndex, gdtItemNo) => {
    try {
      const response = await apiClient.post(`/tabular/${fileId}/${variantIndex}/demote-gdt`, { gdt_item_no: gdtItemNo });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to move GD&T row to Dimensions for Review');
    }
  },

  /** Move a main-table dimension to Low-Confidence for a NORMAL scan. */
  demoteResultsRow: async (fileId, specification, location = '') => {
    try {
      const response = await apiClient.post(`/results/${fileId}/demote`, { specification, location });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to move dimension to Low Confidence');
    }
  },

  /** Move a main-table dimension to Low-Confidence for a TABULAR part. */
  demoteTabularRow: async (fileId, variantIndex, specification, location = '') => {
    try {
      const response = await apiClient.post(`/tabular/${fileId}/${variantIndex}/demote`, { specification, location });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to move dimension to Low Confidence');
    }
  },

  /** Promote a low-confidence row to Main/GD&T/Notes for a TABULAR part. */
  promoteTabularRow: async (fileId, variantIndex, lcIndex, destination, overrides = {}) => {
    try {
      const response = await apiClient.post(`/tabular/${fileId}/${variantIndex}/promote`, {
        lc_index: lcIndex, destination, overrides,
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to share row');
    }
  },

  /**
   * Detect the appropriate scan type (normal/tabular) for a list of already-uploaded files.
   * @param {number[]} fileIds  - IDs returned by /upload
   * @param {string}   apiKey   - Gemini API key
   * @returns {Promise<Array<{file_id, detected_type, confidence, reasoning, error}>>}
   */
  detectScanTypes: async (fileIds, apiKey) => {
    try {
      const response = await apiClient.post('/detect-scan-types', {
        file_ids: fileIds,
        api_key: apiKey,
      });
      return response.data;
    } catch (error) {
      throw toClientError(error, 'Failed to detect scan types');
    }
  },

  // ── Cache helpers (no-ops; callers unchanged; responses always fetched fresh) ─
  getCached: () => null,
  invalidateCache: () => {},
};

export default API;
